import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { useUpdateAppTokens } from '@/hooks/useUpdateAppTokens';
import { AppToken } from '@/services/AppTokensService';

interface TokenItemProps {
  token: AppToken;
  onDelete: (token: AppToken) => void;
}

export function TokenItem({ token, onDelete }: TokenItemProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.AppTokens.components.TokenItem',
  });
  const { updateAppToken } = useUpdateAppTokens();

  async function handleToggleActiveStatus(token: AppToken) {
    try {
      await updateAppToken({
        id: token.id,
        update: { active: !token.active },
      });
    } catch (error) {
      toast.error(t('toggleError'));
    }
  }

  return (
    <div className="flex items-center justify-between border-b p-6 last:border-0">
      <div>
        <h3 className="font-medium">{token.name}</h3>

        <p className="text-sm text-muted-foreground">
          {t('createdOn')}{' '}
          {new Date(token.createdAt)
            .toLocaleString('pt-BR', {
              dateStyle: 'short',
              timeStyle: 'short',
            })
            .replace(',', '')}
        </p>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={token.active}
            onCheckedChange={() => handleToggleActiveStatus(token)}
          />

          <span className="text-sm">
            {token.active ? t('active') : t('inactive')}
          </span>
        </div>

        <Button
          variant="destructive"
          size="icon"
          onClick={() => onDelete(token)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
